import { autoinject, bindable, Container } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { saveAs } from 'file-saver';
import { PubSub } from 'lib/event/PubSub';
import { AsyncErrorHandler, ComponentLoadingFader } from 'lib/ui';
import { MooringAnchorLine, MooringComponentType, MooringReviewDto, UpdateMooringAnchorLineDto } from 'models/mooring';
import { ToastService } from 'services';
import { MooringOfferService, MooringService } from 'services/mooring';
import { OfferViewContext } from '../../OfferViewContext';

@autoinject
export class AnchorLinesContainer {
  @bindable
  protected mooringId: number;

  @bindable
  protected offerId: number;

  @bindable
  protected reviewId?: number;
  protected review: MooringReviewDto;

  protected context = OfferViewContext.useContext(this.container);

  protected controlView: boolean;

  protected anchorLines: MooringAnchorLine[] = [];
  constructor(
    private container: Container,
    protected element: Element,
    private mooringService: MooringService,
    private toaster: ToastService,
    private t: I18N,
    private pubsub: PubSub,
    private confirmation: ConfirmDialog,
    private mooringOfferService: MooringOfferService
  ) {
    pubsub.sub('entity:updated', () => {
      void this.getAnchorLines();
    });

    pubsub.sub('list-entity:updated', () => {
      void this.getAnchorLines();
    });
  }

  protected toggleControlView() {
    this.controlView = !this.controlView;
  }

  @AsyncErrorHandler
  protected async exportAnchorLines() {
    const blob = await this.mooringService.exportProductionAnchorLines(this.mooringId);
    saveAs(blob.blob, blob.fileName);
  }

  @AsyncErrorHandler
  @ComponentLoadingFader
  async attached() {
    if (!this.mooringId) {
      throw new Error('mooringId is required => mooring-id.bind="<id>"');
    }

    await this.getAnchorLines();
  }

  @AsyncErrorHandler
  protected async getAnchorLines() {
    const { data } = await this.mooringService.getAnchorLines(this.mooringId);
    const anchorLines = data;
    if (anchorLines.length) {
      for (const line of anchorLines) {
        line.Components?.sort((a, b) => a.SortIndex - b.SortIndex);
      }
      setTimeout(() => {
        this.anchorLines = anchorLines;
      }, 10);
    }

    this.review = this.context.getState().offerReview;
  }

  @AsyncErrorHandler
  protected async onSaveAnchorLines({ entries, canNavigateOnSave }: { entries: MooringAnchorLine[]; canNavigateOnSave: boolean }) {
    const dto = entries.map(
      (line) =>
        ({
          Id: line.Id,
          Slope: line.Slope,
          BuoyId: line.BuoyId,
          BuoyNo: line.BuoyNo,
          MoorlogNo: line.MoorlogNo,
          DimForce: line.DimForce,
          AnalysisNo: line.AnalysisNo,
          IsAccident: line.IsAccident,
          TotalLength: line.TotalLength ? line.TotalLength : 0,
          LengthOfRope: line.LengthOfRope ? line.LengthOfRope : 0,
          StopPositionLat: line.StopPositionLat,
          StartPositionLat: line.StartPositionLat,
          StopPositionLong: line.StopPositionLong,
          MblAnchoring: line.MblAnchoring,
          StartPositionLong: line.StartPositionLong,
          LengthOfBottomRope: line.LengthOfBottomRope ? line.LengthOfBottomRope : 0,
          MblRopes: line.MblRopes,
          MblSinkRopes: line.MblSinkRopes,
          MblStraps: line.MblStraps,
          BottomAnchoringUplift: line.BottomAnchoringUplift,
          DepthAtAnchoringPoint: line.DepthAtAnchoringPoint,
          HoldingPowerAnchoring: line.HoldingPowerAnchoring,
          MblChainShackleConnectionPlates: line.MblChainShackleConnectionPlates,
          MblUsedChainRopeWithKnot: line.MblUsedChainRopeWithKnot,
          Components: line.Components,
        }) satisfies UpdateMooringAnchorLineDto
    );

    await this.mooringService.updateAnchorLines(this.mooringId, dto);
    this.toaster.showUpdated();
    this.pubsub.publish('list-entity:updated', { name: MooringComponentType.anchorLine });
    if (canNavigateOnSave) {
      this.pubsub.publish('tabs:change-guard-ok', null);
    }
  }

  protected async setReviewStatus(reviewed: boolean) {
    const confirmed = await this.confirmation.confirmYesCancel(
      'dialog.areYouSure',
      reviewed
        ? this.t.tr('general.markAsCheckedConfirmationText', { x: this.t.tr('mooring.anchorLine_plural').toLowerCase() })
        : this.t.tr('general.markAsUnCheckedConfirmationText', { x: this.t.tr('mooring.anchorLine_plural').toLowerCase() })
    );

    if (!confirmed) return;

    await this.mooringOfferService.updateReviewStep(this.reviewId, { Step: 'ANCHORLINES', Reviewed: reviewed });
    this.toaster.showSuccess(reviewed ? 'mooring.setAsChecked' : 'mooring.setAsNotChecked');
    this.pubsub.publish('entity-parent:updated', null);
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}
